import { useState } from 'react';
import moment from 'moment';
import { Box, Flex, Image, Table, Tbody, Td, Th, Thead, Tr } from '@chakra-ui/react';

import { brandLogoMap } from 'Asset/image/brand';
import { TrashOutlineIcon } from 'Asset/icon';
import BadgeArea from 'Component/badge-area';
import BadgeEcomerce from 'Component/badge-ecommerce';
import { Condition, Iteration } from 'Component/others';
import { TextMedium, TextSemiBold, TextRegular } from 'UiKit/typography';
import InputSelectComponent from 'UiKit/input-select';
import useGetScheduleHostList from 'Domain/schedule/useGetScheduleHostList';

import { useScheduleChangeHostState } from 'View/schedule-change-host/state';
import useDeleteConfirmationModal from 'View/schedule-change-host/hooks/useDeleteConfirmationModal';

import styles from './styles.module.css';

const th = (modifier) => `${styles['th']} ${styles[`th--${modifier}`]}`;
const td = (modifier) => `${styles['td']} ${styles[`td--${modifier}`]}`;

const ScheduleRow = ({ schedule, brand, isFirst, rowLength }) => {
  const [currentOpenBrand, setCurrentOpenBrand] = useState(null);
  const { rowErrors, updateState } = useScheduleChangeHostState();
  const { openDeleteConfirmation } = useDeleteConfirmationModal();

  const handleDelete = (scheduleDetailId) => {
    openDeleteConfirmation(() => {
      updateState((prev) => ({
        scheduleChangeHostData: prev.scheduleChangeHostData.map((brandItem) => ({
          ...brandItem,
          details: brandItem.details.map((detail) => (detail.id === scheduleDetailId ? { ...detail, status: 'delete' } : detail)),
        })),
      }));
    });
  };

  const handleChangeHost = (scheduleDetailId, selectedHosts) => {
    updateState((prev) => {
      const newErrors = { ...prev.rowErrors };
      delete newErrors[scheduleDetailId];
      return {
        scheduleChangeHostData: prev.scheduleChangeHostData.map((brandItem) => ({
          ...brandItem,
          details: brandItem.details.map((detail) => (detail.id === scheduleDetailId ? { ...detail, status: 'update', selectedHosts } : detail)),
        })),
        rowErrors: newErrors,
      };
    });
  };

  const startTime = moment(schedule.startDate).format('HH:mm');
  const endTime = moment(schedule.endDate).format('HH:mm');
  const date = moment(schedule.startDate).format('DD MMM YYYY');

  const { data: optionsHost } = useGetScheduleHostList({
    payload: {
      brandName: currentOpenBrand,
      startedAt: schedule.startDate,
      endedAt: schedule.endDate,
    },
  });

  return (
    <Tr className={styles['tr']}>
      <Condition
        statement={isFirst}
        children={
          <Td rowSpan={rowLength} className={td('brand')}>
            <Image src={brandLogoMap[brand]} alt={brand} className={styles['brand__logo']} />
          </Td>
        }
      />
      <Td className={td('time')}>
        <TextSemiBold className={styles['schedule__time']}>
          {startTime} - {endTime}
        </TextSemiBold>
        <TextRegular className={styles['schedule__date']}>{date}</TextRegular>
      </Td>
      <Td className={td('jadwal')}>
        <TextMedium className={styles['schedule__live-name']}>{schedule.liveName}</TextMedium>
        <Flex className={styles['schedule__accounts']}>
          <Iteration
            items={schedule.accountLivestreams}
            render={(account) => <BadgeEcomerce key={account.id} ecommerce={account?.ecommerce} accountName={account?.name} />}
          />
        </Flex>
      </Td>
      <Td className={td('location')}>
        <BadgeArea studioData={schedule.studio} />
      </Td>
      <Td className={td('host')}>
        <InputSelectComponent
          className={styles['host-select']}
          placeholder="Pilih Host"
          minHeight="34px"
          isMulti={true}
          value={schedule.selectedHosts}
          options={optionsHost}
          onChange={(selected) => handleChangeHost(schedule.id, selected)}
          menuPortalTarget={document.body}
          menuPosition="fixed"
          onMenuOpen={() => setCurrentOpenBrand(brand)}
          error={rowErrors?.[schedule.id]}
        />
        <Condition statement={rowErrors?.[schedule.id]}>
          <TextRegular className={styles['host-select__error']}>{rowErrors[schedule.id]}</TextRegular>
        </Condition>
      </Td>
      <Td className={td('action')}>
        <Box className={styles['delete-button']} onClick={() => handleDelete(schedule.id)}>
          <TrashOutlineIcon size="16px" />
        </Box>
      </Td>
    </Tr>
  );
};

const ScheduleTable = ({ schedules }) => {
  return (
    <Box className={styles['schedule-table']}>
      <Box className={styles['schedule-table__scroll']}>
        <Table className={styles['table']}>
          <Thead className={styles['table__head']}>
            <Tr>
              <Th className={th('jadwal')} colSpan={4}>
                Jadwal
              </Th>
              <Th className={th('host')}>Host</Th>
              <Th className={th('action')}>Action</Th>
            </Tr>
          </Thead>
          <Tbody>
            <Iteration
              items={schedules}
              render={(schedule) => {
                const activeDetails = schedule?.details?.filter((detail) => detail.status !== 'delete');
                return (
                  <Iteration
                    items={activeDetails}
                    render={(detail, i) => {
                      return <ScheduleRow key={detail.id} schedule={detail} brand={schedule?.brandName} isFirst={i === 0} rowLength={activeDetails?.length} />;
                    }}
                  />
                );
              }}
            />
          </Tbody>
        </Table>
      </Box>
    </Box>
  );
};

export default ScheduleTable;
